import { OrgBaseInterface } from "../Shared/OrgBase.Interace";

/**
 * Test Caller (ENG-2866): one admin action attaching a tester's phone number
 * to a REAL account on an org, so QA/sales can call the bot as that account.
 * A row is the audit record and the undo anchor: `createdAt` is the start of
 * the test window (undo deletes bot artifacts created after it), `undoneAt`
 * closes the mapping. See docs/snowy/plans/2026-07-07-test-caller-admin-tool.md.
 */
export interface TestCallerMappingInterface extends OrgBaseInterface {
  id: number;
  /** The tester's phone (E.164) that was attached. */
  phoneNumber: string;
  /** The real account (user row) the phone was attached to. */
  userId: number;
  /** Admin who applied the mapping. */
  appliedByMemberId: number;
  /** Set when the mapping is undone; null while active. */
  undoneAt?: Date | null;
  undoneByMemberId?: number | null;

  // ── Temporary identity override (optional) ─────────────────────────────
  // While attached, the tester can overwrite the account's name/email; the
  // originals are snapshotted here and restored on undo/reset/re-apply.
  overrideName?: string | null;
  overrideEmail?: string | null;
  originalName?: string | null;
  originalEmail?: string | null;

  // ── Main-number swap ────────────────────────────────────────────────────
  // Apply makes the tester's phone the account's SINGULAR phoneNumber (the
  // unique, verification-bearing column) after snapshotting the original,
  // which moves into the phoneNumbers array for the window (keeps FMS sync
  // matching + the real tenant's inbound resolution working). Restored on
  // undo/reset/re-apply. `phoneSwapped` distinguishes a recorded-null
  // original from rows created before this existed.
  phoneSwapped?: boolean;
  originalPhoneNumber?: string | null;
}

/** Candidate filters for picking a real account — NOT data shapes to create. */
export enum TestCallerType {
  /** Exactly one active lease. */
  Tenant = "tenant",
  /** Two or more active leases. */
  MultiLease = "multi-lease",
  /** An active lease that is past due. */
  PastDue = "past-due"
}
